import React, {ComponentType, createContext, useContext, useRef} from 'react';
import {FormViewerHandle} from '@wix/form-viewer/widget';
import {useControllerProps} from '../../../../Widget/ControllerContext';

export type FormRegistration = {
  formRef: React.RefObject<FormViewerHandle>;
  focusOnForm: VoidFunction;
};

export type AdditionalDetailsDataContextType = {
  registerForm: (id: string, registration: FormRegistration) => void;
  unregisterForm: (id: string) => void;
  validateAllForms: () => Promise<boolean>;
  continueToNextStep: () => void;
};

export const AdditionalDetailsDataContext = createContext({} as AdditionalDetailsDataContextType);

export function withAdditionalDetailsData<T extends object>(Component: ComponentType<T>) {
  return function Wrapper(props: T) {
    const formsDataByIdRef = useRef<Record<string, FormRegistration>>({});

    const {
      formsStore: {goToNextStep},
      checkoutStore: {cart},
    } = useControllerProps();

    const registerForm = (id: string, registration: FormRegistration) => {
      formsDataByIdRef.current[id] = registration;
    };

    const unregisterForm = (id: string) => {
      const {[id]: _, ...rest} = formsDataByIdRef.current;
      formsDataByIdRef.current = rest;
    };

    const validateAllForms = async () => {
      const orderedFormIds = cart.forms!.map((form) => form.id);

      const validationResults = await Promise.all(
        orderedFormIds.map(async (formId) => {
          const formData = formsDataByIdRef.current[formId];
          const isValid = formData?.formRef.current ? await formData.formRef.current.validate() : false;
          return {formId, isValid};
        })
      );

      const firstInvalid = validationResults.find((result) => !result.isValid);

      if (firstInvalid) {
        const formData = formsDataByIdRef.current[firstInvalid.formId];
        formData.focusOnForm();

        return false;
      }

      return true;
    };

    const continueToNextStep = () => {
      goToNextStep();
    };

    return (
      <AdditionalDetailsDataContext.Provider
        value={{
          registerForm,
          unregisterForm,
          validateAllForms,
          continueToNextStep,
        }}>
        <Component {...props} />
      </AdditionalDetailsDataContext.Provider>
    );
  };
}

export function useAdditionalDetailsData() {
  return useContext(AdditionalDetailsDataContext);
}
